/**
 * Laying out arena / corral panels along an outline, and connecting panels
 * end to end.
 */
import { generateId } from './measurements';

const PX_PER_FOOT = 40;

/**
 * Build a placed panel or gate from a catalog product
 */
export const makePanel = (product, option, start, end, extra = {}) => ({
  id: generateId(),
  productId: product.id,
  kind: option.kind,
  label: option.label,
  lengthFt: option.lengthFt,
  gateOpeningFt: option.openingFt || null,
  // Gates keep the line's rail height; their own height is the arch / bow
  heightIn: product.heightIn,
  archHeightIn: option.kind === 'gate' ? option.heightIn || null : null,
  rails: product.rails,
  start: { ...start },
  end: { ...end },
  ...extra,
});

/**
 * End point of a panel of `lengthFt` laid from `start` toward `toward`
 */
export const panelEndToward = (start, toward, lengthFt, angleStepDeg = 0) => {
  let angle = Math.atan2(toward.y - start.y, toward.x - start.x);
  if (angleStepDeg > 0) {
    const step = (angleStepDeg * Math.PI) / 180;
    angle = Math.round(angle / step) * step;
  }
  const len = lengthFt * PX_PER_FOOT;
  return { x: start.x + Math.cos(angle) * len, y: start.y + Math.sin(angle) * len };
};

/**
 * Nearest panel end within `radius` (plan px) of `pos`, so panels pin together
 */
export const nearestPanelEnd = (panels, pos, radius, excludeId = null) => {
  let best = null;
  let bestDist = radius;
  (panels || []).forEach(p => {
    if (p.id === excludeId) return;
    [p.start, p.end].forEach(pt => {
      const d = Math.hypot(pt.x - pos.x, pt.y - pos.y);
      if (d < bestDist) { bestDist = d; best = pt; }
    });
  });
  return best;
};

/**
 * Line an outline (plan points, closed) with panels.
 *   gates: [{ side: edge index, option: gate option }] - near the middle of that side
 * Pass { closed: false } to fence an open run of points instead of a loop.
 * Panels on each stretch are spread evenly so they meet at the corners; the
 * per-side report says how far that is from the panels' true length.
 */
export const fenceOutline = (outline, product, panelOption, gates = [], fenceId = null, { closed = true } = {}) => {
  const panels = [];
  const sides = [];
  const L = panelOption.lengthFt;

  outline.forEach((a, i) => {
    // An open run (e.g. an alley) has no side back from the last point to the first
    if (!closed && i === outline.length - 1) return;
    const b = outline[(i + 1) % outline.length];
    const sideFt = Math.hypot(b.x - a.x, b.y - a.y) / PX_PER_FOOT;
    if (sideFt < 0.5) return;
    const ux = (b.x - a.x) / (sideFt * PX_PER_FOOT);
    const uy = (b.y - a.y) / (sideFt * PX_PER_FOOT);
    const at = (ft) => ({ x: a.x + ux * ft * PX_PER_FOOT, y: a.y + uy * ft * PX_PER_FOOT });

    // Stretches of panels between gates on this side
    const sideGates = gates
      .filter(g => g.side === i && g.option.lengthFt < sideFt)
      .map(g => g.option);
    const stretches = [];
    let cursor = 0;
    sideGates.forEach((gate, k) => {
      // Near the middle of its share of the side, but slid to a whole number
      // of panels from the last break so that stretch needs no cut panel
      const center = (sideFt * (k + 1)) / (sideGates.length + 1);
      const ideal = center - gate.lengthFt / 2;
      const whole = cursor + Math.round((ideal - cursor) / L) * L;
      const g0 = Math.min(Math.max(cursor, whole), sideFt - gate.lengthFt);
      stretches.push([cursor, g0]);
      panels.push(makePanel(product, gate, at(g0), at(g0 + gate.lengthFt), { fenceId }));
      cursor = g0 + gate.lengthFt;
    });
    stretches.push([cursor, sideFt]);

    let panelCount = 0;
    let panelFt = 0; // fence length the panels must cover
    let boughtFt = 0; // total length of the panels used
    // A stretch about one panel long takes whichever of the line's panels fits it best
    const singles = (product.options || []).filter(o => o.kind === 'panel' && o.lengthFt <= L);
    stretches.forEach(([s0, s1]) => {
      const span = s1 - s0;
      if (span < 0.5) return;
      let option = panelOption;
      if (span < L * 1.25 && singles.length) {
        option = singles.reduce((best, o) => (Math.abs(o.lengthFt - span) < Math.abs(best.lengthFt - span) ? o : best), panelOption);
      }
      const n = Math.max(1, Math.round(span / option.lengthFt));
      for (let k = 0; k < n; k++) {
        panels.push(makePanel(product, option, at(s0 + (span * k) / n), at(s0 + (span * (k + 1)) / n), { fenceId }));
      }
      panelCount += n;
      panelFt += span;
      boughtFt += n * option.lengthFt;
    });

    sides.push({
      side: i,
      lengthFt: sideFt,
      panels: panelCount,
      gates: sideGates.length,
      // Positive: panels would have to spread apart (gaps); negative: crowd/bow
      mismatchFt: panelFt - boughtFt,
    });
  });

  return { panels, sides };
};

/**
 * Count placed panels and gates by catalog item, for a bill of materials
 */
export const summarizePanels = (panels, findProduct) => {
  const rows = new Map();
  (panels || []).forEach(p => {
    const key = `${p.productId}|${p.label}|${p.lengthFt}`;
    if (!rows.has(key)) {
      const product = findProduct(p.productId);
      rows.set(key, {
        key,
        product,
        label: p.label,
        kind: p.kind,
        lengthFt: p.lengthFt,
        count: 0,
        unitPrice: product?.prices?.[p.label] ?? null,
      });
    }
    rows.get(key).count += 1;
  });
  return [...rows.values()].sort((a, b) => (a.kind === b.kind ? b.lengthFt - a.lengthFt : a.kind === 'panel' ? -1 : 1));
};
